import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { PencilSquareIcon } from '@interstate/components/Icons/PencilSquareIcon';
import { TrashIcon } from '@interstate/components/Icons/TrashIcon';
import { DocumentDuplicateIcon } from '@interstate/components/Icons/DocumentDuplicateIcon';
import { ArrowsUpDownIcon } from '@interstate/components/Icons/ArrowsUpDownIcon';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { CardHeader, ButtonGroup } from './address.styles';
import { extractAddressFields, hasAddressData, hasAddressErrors } from './address.utils';
import type { Address, AddressType } from './address.utils';

type AddressHeaderProps = {
    type: AddressType;
    hasBillingAddress: boolean;
    isEditing: boolean;
    setIsPrimaryEditing: (editing: boolean) => void;
    setIsBillingEditing: (editing: boolean) => void;
};

/**
 * Header row for an address card containing the title and action buttons.
 *
 * View mode:  Edit + (Copy to Billing | Delete) + Swap
 * Edit mode:  (Copy to Billing | Delete) + Swap (Edit hidden while editing)
 */
const AddressHeader = ({ type, hasBillingAddress, isEditing, setIsPrimaryEditing, setIsBillingEditing }: AddressHeaderProps) => {
    const { formValues, formErrors, handleSetValues, handleClearBillingAddress, isLocked } = useCustomerCardContext();
    const title = type === 'primary' ? 'Primary' : 'Billing';

    const primaryHasErrors = hasAddressErrors(formErrors);
    const billingHasErrors = hasAddressErrors(formErrors?.billingAddress);
    const anyHasErrors = primaryHasErrors || billingHasErrors;

    const currentPrimary = extractAddressFields(formValues as Address);
    const currentBilling = extractAddressFields((formValues.billingAddress as Address) ?? {});
    const primaryHasData = hasAddressData(currentPrimary);

    const handleEdit = () => {
        if (type === 'primary') setIsPrimaryEditing(true);
        else setIsBillingEditing(true);
    };

    const handleCopyToBilling = () => {
        handleSetValues({ billingAddress: currentPrimary });
        setIsBillingEditing(false);
    };

    const handleDelete = () => {
        handleClearBillingAddress();
        setIsBillingEditing(false);
    };

    const handleSwap = () => {
        handleSetValues({ ...currentBilling, billingAddress: currentPrimary });
        setIsPrimaryEditing(false);
        setIsBillingEditing(false);
    };

    const renderBillingToggle = () =>
        type === 'billing' ? (
            <Button
                buttonStyle="tertiary"
                size="small"
                onClick={handleDelete}
                disabled={isLocked}
                data-testid={`address-card-delete-${type}`}
            >
                Delete
                <TrashIcon data-testid={`address-card-delete-icon-${type}`} />
            </Button>
        ) : primaryHasData ? (
            <Button
                buttonStyle="tertiary"
                size="small"
                onClick={handleCopyToBilling}
                disabled={isLocked || primaryHasErrors}
                data-testid={`address-form-copy-to-billing-${type}`}
            >
                Copy to Billing
                <DocumentDuplicateIcon data-testid={`address-form-copy-to-billing-icon-${type}`} />
            </Button>
        ) : null;

    return (
        <CardHeader>
            <Typography variant="h6">{title}</Typography>
            <ButtonGroup>
                {renderBillingToggle()}
                {hasBillingAddress && (
                    <Button
                        buttonStyle="tertiary"
                        size="small"
                        onClick={handleSwap}
                        disabled={isLocked || anyHasErrors}
                        data-testid={`address-card-swap-${type}`}
                    >
                        Swap
                        <ArrowsUpDownIcon data-testid={`address-card-swap-icon-${type}`} />
                    </Button>
                )}
                {!isEditing && (
                    <Button
                        buttonStyle="tertiary"
                        size="small"
                        onClick={handleEdit}
                        disabled={isLocked}
                        data-testid={`address-card-edit-${type}`}
                    >
                        Edit
                        <PencilSquareIcon data-testid={`address-card-edit-icon-${type}`} />
                    </Button>
                )}
            </ButtonGroup>
        </CardHeader>
    );
};

export default AddressHeader;
